import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export const SERVICES = [
  {
    id: "performance-marketing",
    title: "Performance Marketing",
    category: "Paid media",
    icon: "TrendingUp",
    desc: "Data-driven paid campaigns across Meta, Google & LinkedIn engineered to scale ROAS, not just spend.",
    tags: ["ROAS", "PPC", "Retargeting"],
    image: "https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "digital-marketing",
    title: "Digital Marketing",
    category: "Full funnel",
    icon: "Target",
    desc: "Full-funnel strategy — SEO, content and email — that turns strangers into loyal, paying customers.",
    tags: ["SEO", "Content", "Email"],
    image: "https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "website-development",
    title: "Website Development",
    category: "Engineering",
    icon: "Code2",
    desc: "Blazing-fast, conversion-obsessed websites built to make your brand impossible to ignore.",
    tags: ["React", "Next.js", "CRO"],
    image: "https://images.pexels.com/photos/11035380/pexels-photo-11035380.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "social-media",
    title: "Social Media Management",
    category: "Community",
    icon: "Share2",
    desc: "Thumb-stopping creative and community management that keeps your brand at the top of the feed.",
    tags: ["Content", "Community", "Reels"],
    image: "https://images.pexels.com/photos/1092671/pexels-photo-1092671.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "mobile-app",
    title: "Mobile App Development",
    category: "Product",
    icon: "Smartphone",
    desc: "Native-feel iOS & Android apps with pixel-perfect UX that users actually want to open daily.",
    tags: ["iOS", "Android", "UX"],
    image: "https://images.pexels.com/photos/5077047/pexels-photo-5077047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "branding",
    title: "Branding & Identity",
    category: "Brand system",
    icon: "Sparkles",
    desc: "Distinctive brand systems — logos, voice and visuals — that command trust and premium pricing.",
    tags: ["Identity", "Strategy", "Design"],
    image: "https://images.pexels.com/photos/1762851/pexels-photo-1762851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const SERVICE_DETAILS = {
  "performance-marketing": {
    label: "Paid media, engineered",
    tagline: "Every rupee spent, accounted for.",
    intro: "We treat your ad budget like our own money. Full-funnel paid media on Meta, Google, LinkedIn and programmatic — structured around LTV economics, aggressive testing velocity and creative systems that keep learning phases alive.",
    what: [
      { h: "Account architecture", p: "Rebuild campaigns from first principles — clean event tracking, proper attribution, sane naming, no legacy debt." },
      { h: "Creative systems", p: "30+ ad concepts per month across static, motion and UGC — engineered for hooks, not hopes." },
      { h: "Testing velocity", p: "Weekly experiments on audiences, hooks, offers and landers — every winner scaled, every loser killed fast." },
      { h: "Landing pages", p: "Purpose-built landing pages with server-side tracking and conversion-first structure — because bad LPs kill great ads." },
    ],
    deliverables: [
      "Full paid-media audit + growth roadmap",
      "Multi-channel campaign build & launch",
      "Weekly creative refresh (min. 8 concepts)",
      "Bi-weekly performance reviews with founder",
      "Monthly business report tied to contribution margin",
    ],
    for: "Ecommerce and D2C brands doing ₹40L–₹4Cr in monthly revenue, SaaS teams with a validated funnel, and service businesses ready to scale beyond referrals.",
    kpis: ["Blended ROAS", "CAC vs LTV", "Contribution margin", "Payback period"],
  },
  "digital-marketing": {
    label: "Full-funnel growth",
    tagline: "SEO, content and email — the growth compound.",
    intro: "Paid ads are rented traffic. Digital marketing is the layer that compounds — organic search, content, email, community. We build the engine that keeps working after you pause the ads.",
    what: [
      { h: "SEO strategy", p: "Technical audits, keyword-intent mapping and content architecture designed for commercial intent, not vanity traffic." },
      { h: "Content engine", p: "Founder-led thought leadership and pillar content that ranks and converts — not AI slop." },
      { h: "Email &amp; lifecycle", p: "Klaviyo, Mailchimp and Customer.io flows that turn one purchase into a relationship." },
      { h: "Analytics", p: "One dashboard where every channel ladders up to revenue — no more channel finger-pointing." },
    ],
    deliverables: [
      "SEO audit + 12-month content roadmap",
      "Monthly editorial calendar &amp; publishing",
      "Email flow architecture &amp; campaigns",
      "Unified analytics dashboard",
      "Monthly growth review",
    ],
    for: "Founders who want to stop renting attention and start owning it — early-stage brands, mature D2C, and B2B teams with a real product story.",
    kpis: ["Organic sessions", "Email revenue %", "SQLs from content", "Blended CAC"],
  },
  "website-development": {
    label: "Engineering for marketers",
    tagline: "Websites that respect marketing decisions.",
    intro: "Most agency websites look pretty and convert terribly. We engineer sites in React, Next.js and Shopify with speed, accessibility and CRO baked in — because a slow site quietly bleeds every rupee you spend on ads.",
    what: [
      { h: "Design + build", p: "Custom design systems shipped as pixel-perfect React or Shopify Liquid — no themes, no plugin soup." },
      { h: "Core Web Vitals", p: "LCP, INP and CLS engineered green from launch. Every 100ms matters." },
      { h: "CMS &amp; workflow", p: "Sanity, Contentful or headless Shopify so your team can ship without a developer in the loop." },
      { h: "Ongoing CRO", p: "A/B testing, funnel analytics and quarterly redesigns to keep conversion climbing." },
    ],
    deliverables: [
      "Custom design + UX system",
      "Production build (React, Next.js, Shopify)",
      "Analytics + server-side tracking",
      "Post-launch CRO retainer (optional)",
      "Documentation &amp; team handover",
    ],
    for: "Brands who realised their website is the product, not a brochure — D2C, SaaS, agencies, professional services.",
    kpis: ["Conversion rate", "LCP / INP", "Bounce on landing", "Revenue per visitor"],
  },
  "social-media": {
    label: "Content velocity",
    tagline: "Feed-worthy content, on a system.",
    intro: "Instagram and LinkedIn reward volume, taste and consistency. We plug in a dedicated social team — strategist, editor, creators — and ship on a real calendar, week after week.",
    what: [
      { h: "Strategy", p: "Audience research, content pillars and a monthly narrative that stacks — not one-off posts." },
      { h: "Creative production", p: "Reels, Shorts, carousels, static — shot, edited and posted by an in-house creative studio." },
      { h: "Community management", p: "DMs, comments and reputation handled by humans who understand your brand voice." },
      { h: "Founder-led content", p: "For founders who want to build in public without spending 15 hours a week filming." },
    ],
    deliverables: [
      "Monthly content strategy",
      "20+ posts per month (reels + static)",
      "Community management (mon–sat)",
      "Weekly reporting",
      "Quarterly creative refresh",
    ],
    for: "D2C brands, personal-brand founders, and B2B teams that know their next customer is scrolling right now.",
    kpis: ["Follower growth", "Saves &amp; shares", "Story replies", "Attributed sales"],
  },
  "mobile-app": {
    label: "Native product, marketed native",
    tagline: "Apps built to be opened daily.",
    intro: "We design and build iOS + Android apps end-to-end — from Figma to App Store — and pair them with the paid + ASO strategy to make sure they don't rot in a folder.",
    what: [
      { h: "Product design", p: "Discovery workshops, user journeys and high-fidelity Figma prototypes ready for build." },
      { h: "Cross-platform build", p: "React Native or Flutter for the fastest path to shipping without sacrificing feel." },
      { h: "Launch marketing", p: "App Store Optimization, paid UA campaigns and referral loops on day one." },
      { h: "Retention &amp; analytics", p: "Onboarding, push and in-app messaging systems that turn installs into DAU." },
    ],
    deliverables: [
      "Product discovery + Figma design",
      "iOS &amp; Android build (React Native)",
      "App Store &amp; Play Store submission",
      "Launch UA campaign",
      "3-month post-launch optimisation",
    ],
    for: "Consumer startups, community platforms, D2C brands with a loyalty use-case, and services with heavy repeat behaviour.",
    kpis: ["Install-to-DAU", "D30 retention", "In-app revenue", "Rating &amp; reviews"],
  },
  "branding": {
    label: "Brand system",
    tagline: "The identity that earns premium pricing.",
    intro: "Great performance marketing amplifies whatever you've built underneath. If the brand feels generic, ads only accelerate that. We craft distinctive identity systems — naming, logo, voice, visual language — that let you charge more, hire better and stand out on a saturated shelf.",
    what: [
      { h: "Positioning", p: "Category, audience and promise nailed down in one crisp sentence your whole company can repeat." },
      { h: "Identity system", p: "Wordmark, monogram, colour, type and motion — designed as a system, not five separate ideas." },
      { h: "Voice &amp; messaging", p: "Tone of voice, key messages and copy templates for ads, site and social." },
      { h: "Brand guidelines", p: "A living Figma library your team, designers and agency partners actually use." },
    ],
    deliverables: [
      "Positioning &amp; naming (if new)",
      "Complete identity system",
      "Voice &amp; messaging playbook",
      "Brand guidelines (Figma)",
      "6 weeks of design support post-launch",
    ],
    for: "Founders about to fundraise, rebrand, or launch a new category — and mature brands whose identity hasn't kept up with their ambition.",
    kpis: ["Unaided brand recall", "Direct traffic %", "Premium price tolerance", "Time-to-close"],
  },
};

export const REVIEWS = [
  {
    name: "Rajesh Khanna",
    role: "Founder, Spice Route Foods",
    location: "Mumbai, India",
    rating: 5,
    text: "Adinvo took our D2C brand from ₹4L to ₹38L monthly revenue in under a year. Their performance marketing team treats our budget like it's their own money. Genuinely the best agency we've worked with.",
    img: "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Sarah Lindqvist",
    role: "CMO, NordicWear",
    location: "London, UK",
    rating: 5,
    text: "We hired Adinvo for a website rebuild and stayed for everything else. Conversion rate jumped 63% post-launch. Their attention to craft and data is world-class — you'd never guess the price point.",
    img: "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Ananya Mehta",
    role: "Co-founder, Bloom Skincare",
    location: "Bengaluru, India",
    rating: 5,
    text: "Five years of expertise really shows. Their social media team rebuilt our entire content engine and we hit 210K followers in 8 months. Every rupee spent came back multiplied.",
    img: "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Michael Torres",
    role: "CEO, Vantage Fintech",
    location: "Austin, USA",
    rating: 5,
    text: "Adinvo built our mobile app and ran the launch campaign end to end. 50K downloads in the first month with a CAC well below target. Responsive, sharp and refreshingly honest.",
    img: "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Priya Nair",
    role: "Director, Aarna Jewels",
    location: "Mumbai, India",
    rating: 5,
    text: "The branding overhaul completely repositioned us in the premium segment. We now command 40% higher pricing. Adinvo understands Indian and global audiences equally well.",
    img: "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const PHONE = "+919316780441";
export const PHONE_DISPLAY = "+91 93167 80441";
export const WHATSAPP_MSG = encodeURIComponent(
  "Hi Adinvo! I'd like to know more about your marketing services."
);
